import type { LocaleBundle } from '../types.js'

export const en: LocaleBundle = {
  code: 'en',
  name: 'English',
  dict: {
    'common:appName': 'Modern Admin',
    'common:home': 'Home',
    'common:search': 'Search',
    'common:loading': 'Loading…',
    'common:save': 'Save',
    'common:cancel': 'Cancel',
    'common:delete': 'Delete',
    'common:create': 'Create',
    'common:edit': 'Edit',
    'common:show': 'Show',
    'common:back': 'Back',
    'common:noRecords': 'No records found.',
    'common:noRecordsHint': 'Get started by creating a new {resource}.',
    'common:noMatchingRecords': 'No records match your filters.',
    'common:clearFilters': 'Clear filters',
    'common:confirmDelete': 'Delete this record?',
    'common:confirmAction': 'Confirm action',
    'common:confirm': 'Confirm',
    'common:perPage': 'Per page',
    'common:page': 'Page',
    'common:of': 'of',
    'common:total': 'Total',
    'auth:login': 'Sign in',
    'auth:logout': 'Sign out',
    'auth:email': 'Email',
    'auth:password': 'Password',
    'auth:loginFailed': 'Invalid credentials.',
    'errors:notFound': 'Not found.',
    'errors:forbidden': 'You do not have access to this resource.',
    'errors:server': 'Something went wrong. Please retry.',
    'common:resources': 'Resources',
    'common:actions': 'Actions',
    'action:notFound': 'Action "{action}" is not available on this resource.',
    'action:componentMissing': 'No component named "{component}" is registered.',
    'action:componentMissingHint': 'Register it on the ComponentLoader passed to <ModernAdminProvider components={…}>.',
    'action:loadFailed': 'Failed to load the action.',
    'common:new': 'New',
    'common:rowsPerPage': 'Rows per page',
    'common:filter': 'Filter {label}…',
    'validation:required': '{label} is required',
    'validation:invalidNumber': '{label} must be a valid number',
    'validation:invalidInteger': '{label} must be an integer',
    'validation:invalidEmail': '{label} must be a valid email',
    'validation:invalidUrl': '{label} must be a valid URL',
    'validation:invalidDate': '{label} must be a valid date',
    'validation:invalidUuid': '{label} must be a valid UUID',
    'validation:tooShort': '{label} must be at least {min} characters',
    'validation:tooLong': '{label} must be at most {max} characters',
    'validation:notInList': '{label} has an invalid value',
    'validation:emptySelection': 'Select at least one {label}',
    'toast:created': 'Created successfully',
    'toast:saved': 'Saved successfully',
    'toast:deleted': 'Deleted successfully',
    'toast:createFailed': 'Failed to create',
    'toast:saveFailed': 'Failed to save',
    'toast:deleteFailed': 'Failed to delete',
    'toast:loadFailed': 'Failed to load',
    'toast:validationFailed': 'Please fix the errors and try again',
    'common:close': 'Close',
    'common:export': 'Export',
    'export:title': 'Export records',
    'export:description': 'Download all matching records of {resource} as a single file.',
    'export:csvHint': 'Comma-separated values',
    'export:jsonHint': 'Structured JSON array',
    'export:downloading': 'Preparing download…',
    'export:progress': 'Loaded {loaded} of {total}…',
    'export:exported': 'Exported {count} records',
    'export:exportFailed': 'Failed to export records',
    'common:apply': 'Apply',
    'common:clear': 'Clear',
    'common:filters': 'Filters',
    'common:columns': 'Columns',
    'common:toggleColumns': 'Toggle columns',
    'common:openMenu': 'Open menu',
    'common:refresh': 'Refresh',
    'common:copy': 'Copy',
    'common:copied': 'Copied!',
    'common:applyFilters': 'Apply filters',
    'common:clearAll': 'Clear all',
    'common:any': 'Any',
    'common:yes': 'Yes',
    'common:no': 'No',
    'common:filterPlaceholder': 'Filter…',
    'common:loadFailed': 'Failed to load: {error}',
    'common:theme': 'Theme',
    'common:language': 'Language',
    'common:toggleTheme': 'Toggle theme',
    'common:clearSelection': 'Clear selection',
    'common:searchPlaceholder': 'Search…',
    'home:searchResources': 'Search resources…',
    'home:noMatchingResources': 'No matching resources found.',
    'globalSearch:title': 'Global search',
    'globalSearch:description': 'Search across all resources and records.',
    'globalSearch:trigger': 'Search…',
    'globalSearch:placeholder': 'Search across all resources…',
    'globalSearch:hint': 'Start typing to search across all resources.',
    'globalSearch:noResults': 'No matching records found.',
    'globalSearch:error': 'Search failed. Please try again.',
    'globalSearch:hotkey': 'Open global search',
    'globalSearch:matchedIn': 'in {field}',
    'globalSearch:recent': 'Recent searches',
    'globalSearch:clearRecent': 'Clear',
    'globalSearch:showAllIn': 'Show all in {resource}',
    'common:pageOf': 'Page {page} of {total}',
    'common:firstPage': 'First page',
    'common:previousPage': 'Previous page',
    'common:nextPage': 'Next page',
    'common:lastPage': 'Last page',
    'common:recordsCount': '{count} records',
    'common:selectedCount': '{count} selected',
    'common:selectRow': 'Select row',
    'common:selectAll': 'Select all',
    'common:deleteSelected': 'Delete selected',
    'common:bulkDeleteConfirm': 'Delete {count} selected records?',
    'toast:bulkDeleted': 'Deleted {count} records',
    'toast:bulkDeleteFailed': 'Failed to delete records',
    'common:from': 'From',
    'common:to': 'To',
    'common:sortBy': 'Sort by',
    'common:next': 'Next',
    'common:previous': 'Previous',
    'common:relatedRecords': 'Related records',
    'common:viewAll': 'View all',
    'common:noRelatedRecords': 'No related records.',
    'common:shortcutsHelp': 'Keyboard shortcuts',
    'common:shortcutsEmpty': 'No shortcuts available on this screen.',
    'common:preview': 'Preview',
    'common:download': 'Download',
    'common:openInNewTab': 'Open in new tab',
    'common:format': 'Format',
    'common:invalidJson': 'Invalid JSON',
    'common:showPassword': 'Show password',
    'common:hidePassword': 'Hide password',
    'auth:title': 'Sign in',
    'auth:subtitle': 'Enter your email and password to sign in',
    'auth:tagline': 'The modern admin panel for any stack.',
    'auth:orContinueWith': 'Or continue with',
    'auth:continueWith': 'Continue with {provider}',
    'common:chooseFile': 'Choose file',
    'common:chooseFiles': 'Choose files',
    'common:dragAndDrop': 'Drag and drop or',
    'common:chooseAFile': 'choose a file',
    'common:addMoreFiles': 'add more files',
    'common:uploading': 'Uploading…',
    'common:uploadingFile': 'Uploading {name}…',
    'common:removeFile': 'Remove file',
    'common:uploadFailed': 'Upload failed',
    'common:dismiss': 'Dismiss',
    'common:openCalendar': 'Open calendar',
    'common:time': 'Time',
    'common:downloadFailed': 'Download failed',
    'richtext:bold': 'Bold',
    'richtext:italic': 'Italic',
    'richtext:strikethrough': 'Strikethrough',
    'richtext:inlineCode': 'Inline code',
    'richtext:heading': 'Heading {level}',
    'richtext:bulletList': 'Bullet list',
    'richtext:numberedList': 'Numbered list',
    'richtext:blockquote': 'Blockquote',
    'richtext:horizontalRule': 'Horizontal rule',
    'richtext:insertLink': 'Insert link',
    'richtext:undo': 'Undo',
    'richtext:redo': 'Redo',
    'richtext:source': 'Source ({format})',
    'richtext:splitView': 'Split view',
    'richtext:visualEditor': 'Visual editor',
    'richtext:fullscreen': 'Fullscreen',
    'richtext:exitFullscreen': 'Exit fullscreen',
    'richtext:urlPrompt': 'URL',
    'common:saving': 'Saving…',
    'common:done': 'Done',
    'settings:menuItem': 'Settings',
    'settings:noSectionsConfigured': 'No settings sections are available for this deployment.',
    'settings:apiKeys.title': 'API keys',
    'settings:apiKeys.description': 'Programmatic access tokens for the admin API. Each key carries a permission set that limits which resources and actions it can invoke.',
    'settings:apiKeys.actions.create': 'New API key',
    'settings:apiKeys.actions.edit': 'Edit',
    'settings:apiKeys.actions.revoke': 'Revoke',
    'settings:apiKeys.empty.title': 'No API keys yet',
    'settings:apiKeys.empty.description': 'Create your first key to grant programmatic access.',
    'settings:apiKeys.columns.name': 'Name',
    'settings:apiKeys.columns.start': 'Prefix',
    'settings:apiKeys.columns.permissions': 'Permissions',
    'settings:apiKeys.columns.expiresAt': 'Expires',
    'settings:apiKeys.columns.enabled': 'Enabled',
    'settings:apiKeys.columns.actions': 'Actions',
    'settings:apiKeys.lastUsed': 'Last used {date}',
    'settings:apiKeys.expiresNever': 'Never',
    'settings:apiKeys.permissions.resource': 'Resource',
    'settings:apiKeys.permissions.actions': 'Actions',
    'settings:apiKeys.permissions.selectAll': 'Select all',
    'settings:apiKeys.permissions.clearAll': 'Clear all',
    'settings:apiKeys.permissions.none': 'No permissions',
    'settings:apiKeys.permissions.summary': '{resources} resources · {actions} actions',
    'settings:apiKeys.permissions.noResources': 'No resources are registered.',
    'settings:apiKeys.permissions.selectAllResources': 'Select all resources & actions',
    'settings:apiKeys.permissions.clearAllResources': 'Clear all resources & actions',
    'settings:apiKeys.editor.titleCreate': 'New API key',
    'settings:apiKeys.editor.titleEdit': 'Edit API key',
    'settings:apiKeys.editor.description': 'Pick a descriptive name and grant only the resources and actions this key needs.',
    'settings:apiKeys.editor.name': 'Name',
    'settings:apiKeys.editor.namePlaceholder': 'e.g. CI deploy bot',
    'settings:apiKeys.editor.expiresInDays': 'Expires in (days)',
    'settings:apiKeys.editor.expiresPlaceholder': 'never',
    'settings:apiKeys.editor.expiresHint': 'Leave blank for no expiry. Maximum 3650 days.',
    'settings:apiKeys.editor.permissions': 'Permissions',
    'settings:apiKeys.editor.selectedActions': '{count} selected',
    'settings:apiKeys.created.title': 'API key created',
    'settings:apiKeys.created.description': 'Copy the key now — it will not be shown again.',
    'settings:apiKeys.created.warning': 'This is the only time you will see the full key. Store it somewhere safe.',
    'settings:apiKeys.created.reveal': 'Reveal key',
    'settings:apiKeys.created.hide': 'Hide key',
    'settings:apiKeys.created.copy': 'Copy',
    'settings:apiKeys.created.copied': 'Copied',
    'settings:apiKeys.notice.revoked': 'API key revoked',
    'settings:apiKeys.notice.updated': 'API key updated',
    'settings:apiKeys.notice.copied': 'Copied to clipboard',
    'settings:apiKeys.notice.copyFailed': 'Could not copy to clipboard',
    'settings:apiKeys.confirmRevoke.title': 'Revoke API key',
    'settings:apiKeys.confirmRevoke.description': 'Revoke "{name}"? Any service using this key will stop working immediately.',
    'settings:webhooks.title': 'Webhooks',
    'settings:webhooks.description': 'Send signed HTTP notifications when records are created, updated, or deleted.',
    'settings:webhooks.actions.create': 'New webhook',
    'settings:webhooks.actions.test': 'Test',
    'settings:webhooks.notice.saved': 'Webhook saved',
    'settings:webhooks.notice.deleted': 'Webhook deleted',
    'settings:webhooks.notice.testQueued': 'Test notification queued',
    'settings:webhooks.confirmDelete.title': 'Delete webhook',
    'settings:webhooks.confirmDelete.description': 'Delete "{name}"? Delivery history for this webhook will also be removed.',
    'settings:webhooks.empty.title': 'No webhooks yet',
    'settings:webhooks.empty.description': 'Create a webhook to notify external systems about admin changes.',
    'settings:webhooks.columns.name': 'Name',
    'settings:webhooks.columns.resource': 'Resource',
    'settings:webhooks.columns.events': 'Events',
    'settings:webhooks.columns.enabled': 'Enabled',
    'settings:webhooks.columns.actions': 'Actions',
    'settings:webhooks.deliveries.title': 'Recent deliveries',
    'settings:webhooks.deliveries.status': 'Status',
    'settings:webhooks.deliveries.event': 'Event',
    'settings:webhooks.deliveries.attempt': 'Attempt',
    'settings:webhooks.deliveries.response': 'Response',
    'settings:webhooks.deliveries.createdAt': 'Created',
    'settings:webhooks.editor.titleCreate': 'New webhook',
    'settings:webhooks.editor.titleEdit': 'Edit webhook',
    'settings:webhooks.editor.description': 'Choose target URL, events, filters, and optional payload projection.',
    'settings:webhooks.editor.name': 'Name',
    'settings:webhooks.editor.url': 'URL',
    'settings:webhooks.editor.resource': 'Resource',
    'settings:webhooks.editor.allResources': 'All resources',
    'settings:webhooks.editor.secret': 'Signing secret',
    'settings:webhooks.editor.enabled': 'Enabled',
    'settings:webhooks.editor.events': 'Events',
    'settings:webhooks.editor.payloadFields': 'Payload fields',
    'settings:webhooks.editor.headers': 'Headers',
    'settings:webhooks.editor.headersHint': 'Custom HTTP headers sent with every delivery.\n\nExample:\n{\n  "Authorization": "Bearer <token>",\n  "X-Source": "modern-admin"\n}\n\nUseful for auth tokens, Slack incoming webhook URLs stored as a header, or endpoint-specific signatures.',
    'settings:webhooks.editor.filters': 'Filters',
    'settings:webhooks.editor.filtersHint': 'Deliver only when the record matches all conditions (AND semantics). Leave empty to deliver for every matching event.\n\nExample:\n{\n  "status": "published",\n  "role": "admin"\n}\n\nNote: delete events bypass field filters because the record no longer exists.',
    'settings:webhooks.editor.secretHint': 'Random string used to sign each delivery with HMAC-SHA256. Verify the X-Webhook-Signature header on your server to ensure the request is genuine.',
    'settings:webhooks.editor.payloadFieldsHint': 'Restrict the payload to specific fields. Leave all unchecked to include the full record. Useful to avoid sending sensitive columns.',
    'chart:dashboard': 'Dashboard',
    'chart:addChart': 'Add chart',
    'chart:newChart': 'New chart',
    'chart:editChart': 'Edit chart',
    'chart:deleteChart': 'Remove chart',
    'chart:noCharts': 'No charts yet',
    'chart:noChartsHint': 'Add a chart to start monitoring your data.',
    'chart:addGroup': 'Add group',
    'chart:newGroup': 'New group',
    'chart:editGroup': 'Edit group',
    'chart:deleteGroup': 'Remove group',
    'chart:saveGroup': 'Save group',
    'chart:groupName': 'Group name',
    'chart:groupNamePlaceholder': 'e.g. Sales metrics',
    'chart:groupNameRequired': 'Group name is required',
    'chart:groupOrder': 'Group order',
    'chart:order': 'Order',
    'chart:orderHint': 'Lower numbers come first.',
    'chart:moveToGroup': 'Move to group',
    'chart:moveChart': 'Move chart',
    'chart:moveGroup': 'Group',
    'chart:moveNoGroups': 'No groups yet.',
    'chart:moveNoGroupsHint': 'Create a group first to organize your charts.',
    'chart:deleteChartConfirm': 'Remove this chart?',
    'chart:deleteChartConfirmHint': 'This action cannot be undone.',
    'chart:deleteGroupConfirm': 'Remove this group?',
    'chart:deleteGroupConfirmHint': 'This will permanently delete the group and {count} chart(s) inside it.',
    'chart:title': 'Title',
    'chart:titlePlaceholder': 'e.g. New users per day',
    'chart:untitled': 'Untitled chart',
    'chart:chartType': 'Chart type',
    'chart:resource': 'Resource',
    'chart:groupBy': 'Group by',
    'chart:step': 'Step',
    'chart:metric': 'Metric',
    'chart:aggregateField': 'Aggregate field',
    'chart:selectField': 'Select field…',
    'chart:saveChart': 'Save chart',
    'chart:typeLine': 'Line',
    'chart:typeArea': 'Area',
    'chart:typeBar': 'Bar',
    'chart:typePie': 'Pie',
    'chart:stepDay': 'Day',
    'chart:stepWeek': 'Week',
    'chart:stepMonth': 'Month',
    'chart:stepYear': 'Year',
    'chart:metricCount': 'Count',
    'chart:metricSum': 'Sum',
    'chart:metricAvg': 'Average',
    'chart:noData': 'No data for selected period',
    'chart:loadError': 'Failed to load chart data',
    'chart:value': 'Value',
    'dashboard:vis': 'Visualisation',
    'dashboard:visKpi': 'KPI',
    'dashboard:visLine': 'Line',
    'dashboard:visArea': 'Area',
    'dashboard:visBar': 'Bar',
    'dashboard:visPie': 'Pie',
    'dashboard:metricCount': 'Count',
    'dashboard:metricSum': 'Sum',
    'dashboard:metricAvg': 'Average',
    'dashboard:metricMin': 'Min',
    'dashboard:metricMax': 'Max',
    'dashboard:builder.range': 'Time range',
    'dashboard:builder.filters': 'Filters',
    'dashboard:builder.filtersHint': 'Narrow the dataset before aggregating. Tick a filter to expose it as a quick toggle above the chart.',
    'dashboard:builder.quickFilterHint': 'Expose this filter above the chart on the dashboard.',
    'dashboard:builder.quickFilterToggle': 'Expose {field} as a quick filter',
    'dashboard:range.7d': '7d',
    'dashboard:range.30d': '30d',
    'dashboard:range.90d': '90d',
    'dashboard:range.1y': '1y',
    'dashboard:range.all': 'All',
    'dashboard:range.custom': 'Custom',
    'dashboard:widget.previousPeriod': 'vs. previous period',
    'dashboard:widget.deltaUp': '+{value} ({percent}%)',
    'dashboard:widget.deltaDown': '−{value} ({percent}%)',
    'dashboard:widget.deltaFlat': '{value} ({percent}%)',
    'dashboard:widget.shrink': 'Shrink to half width',
    'dashboard:widget.expand': 'Expand to full row',
    'dashboard:widget.toggleSql': 'Toggle SQL',
    'dashboard:widget.unsupported': 'Charts are not supported for your database yet.',
    'dashboard:widget.truncatedWarning': 'Showing partial data — too many rows to aggregate the full range.',
    'dashboard:widget.showAll': 'Show all',
    'dashboard:widget.hideAll': 'Hide all',
    'dashboard:seriesTotal': 'Total',
    'dashboard:seriesOther': 'Other',
    'dashboard:seriesNull': 'Empty',
    'dashboard:builder.dateField': 'Date field',
    'dashboard:builder.dateFieldHint': 'X-axis bucketing uses this date column.',
    'dashboard:builder.secondaryGroupBy': 'Breakdown',
    'dashboard:builder.noBreakdown': 'Single series',
    'dashboard:builder.topN': 'Max series',
    'dashboard:builder.width': 'Width',
    'dashboard:width.half': 'Half',
    'dashboard:width.full': 'Full row',
    'dashboard:builder.comparePrevious': 'Compare to previous period',
    'dashboard:builder.comparePreviousHint': 'Overlay the equal-length preceding period as a dashed line and show the change next to the title.',
    'dashboard:builder.comparePreviousDisabled': 'Not available with a breakdown.',
    'dashboard:builder.transform': 'Value transform',
    'dashboard:builder.transformHint': 'Applied to values before display — e.g. divide by 100 to show cents as dollars.',
    'dashboard:builder.transformAdd': 'Add step',
    'dashboard:builder.transformRemove': 'Remove step',
    'dashboard:builder.opDivide': 'Divide by',
    'dashboard:builder.opMultiply': 'Multiply by',
    'dashboard:builder.opAdd': 'Add',
    'dashboard:builder.opSubtract': 'Subtract',
    'dashboard:builder.format': 'Value format',
    'dashboard:builder.formatStyle': 'Style',
    'dashboard:builder.formatNumber': 'Number',
    'dashboard:builder.formatCurrency': 'Currency',
    'dashboard:builder.formatPercent': 'Percent',
    'dashboard:builder.formatCurrencyCode': 'Currency code',
    'dashboard:builder.formatDecimals': 'Decimals',
    'dashboard:builder.formatCompact': 'Compact numbers (12.5k)',
    'dashboard:builder.formatPrefix': 'Prefix',
    'dashboard:builder.formatSuffix': 'Suffix',
    'dashboard:builder.color': 'Series color',
    'dashboard:widget.previousSeries': '{label} (previous)',
    'dashboard:widget.editColors': 'Edit colors',
    'dashboard:widget.colorsTitle': 'Series colors',
    'dashboard:widget.colorCustom': 'Custom',
    'dashboard:widget.colorAuto': 'Auto',
    'dashboard:widget.colorPick': 'Pick a color',
    'dashboard:builder.tabData': 'Data',
    'dashboard:builder.tabDisplay': 'Display',
    'dashboard:builder.tabFilters': 'Filters',
    'dashboard:builder.filtersEmpty': 'This resource has no filterable fields.',
    'history:revisions': 'Revisions',
    'history:noRevisions': 'No revisions yet',
    'history:loadError': 'Failed to load revision history',
    'history:revert': 'Revert',
    'history:confirmRevert': 'Revert to this revision?',
    'history:revertSuccess': 'Revision restored',
    'history:revertFailed': 'Failed to restore revision',
    'history:unknownUser': 'Unknown user',
    'history:changes': 'changes',
    'history:compareTo': 'Compare to',
    'history:storedDiff': 'Change diff',
    'history:op.create': 'Created',
    'history:op.update': 'Updated',
    'history:op.delete': 'Deleted',
    'diff:added': 'Added',
    'diff:changed': 'Changed',
    'diff:removed': 'Removed',
    'diff:before': 'Before',
    'diff:after': 'After',
    'diff:noChanges': 'No field changes recorded',
    'audit:title': 'Activity',
    'audit:resource': 'Resource',
    'audit:action': 'Action',
    'audit:recordId': 'Record ID',
    'audit:userId': 'User ID',
    'audit:allResources': 'All resources',
    'audit:allActions': 'All actions',
    'audit:noEvents': 'No activity yet',
    'audit:records': 'records',
    'audit:loadError': 'Failed to load activity',
    'audit:by': 'by {user}',
    'audit:viewRecord': 'View record',
    'audit:viewChanges': 'View changes',
    'audit:hideChanges': 'Hide changes',
    'audit:noDiff': 'Change details unavailable',
    'audit:loadDiffError': 'Failed to load changes',
    'audit:action.new': 'Record created',
    'audit:action.edit': 'Record updated',
    'audit:action.delete': 'Record deleted',
    'audit:action.bulkDelete': 'Records deleted',
    'audit:action.login': 'Signed in',
    'audit:action.apiKeyCreate': 'API key created',
    'audit:action.apiKeyUpdate': 'API key updated',
    'audit:action.apiKeyDelete': 'API key deleted',
    'audit:virtualResource.auth': 'Authentication',
    'audit:virtualResource.apiKeys': 'API Keys',
    'audit:prevPage': 'Previous',
    'audit:nextPage': 'Next',
    'audit:dateRangePlaceholder': 'Select date range',
    'keyValue:noMatches': 'No matches — press Enter to keep "{value}".',
    'aiAssistant:queue.cancel': 'Remove from queue',
    'aiAssistant:title': 'AI Assistant',
    'aiAssistant:fab.label': 'Open AI assistant',
    'aiAssistant:fab.short': 'AI',
    'aiAssistant:description': 'Ask questions about records, relationships, reports, and data trends.',
    'aiAssistant:notConfigured.title': 'Assistant is not configured',
    'aiAssistant:notConfigured.description': 'Add the OpenRouter API key in settings to enable the read-only AI assistant.',
    'aiAssistant:notConfigured.openSettings': 'Open settings',
    'aiAssistant:tryAsking': 'Try asking:',
    'aiAssistant:tryAsking.example1': 'Which customers have the most posts?',
    'aiAssistant:tryAsking.example2': 'Show recently created records for a resource.',
    'aiAssistant:tryAsking.example3': 'Explain relationships between specific records.',
    'aiAssistant:thinking': 'Thinking',
    'aiAssistant:thinkingProgress': 'Thinking ({progress}%)',
    'aiAssistant:input.placeholder': 'Ask about your data…',
    'aiAssistant:input.hint': 'Enter to send · Shift+Enter for a new line',
    'aiAssistant:send': 'Send message',
    'aiAssistant:newChat': 'New chat',
    'aiAssistant:history': 'Chat history',
    'aiAssistant:history.empty': 'No previous chats',
    'aiAssistant:noText': 'Here are the related records I found:',
    'aiAssistant:fallback.noToolResult': 'I ran the request, but the model did not return a text answer. Try refining the question or enable debug logs to inspect the tool result.',
    'aiAssistant:fallback.noRows': 'No records were found for this request.',
    'aiAssistant:fallback.rowsFound': 'Rows found: {count}.',
    'aiAssistant:taskFailed': 'AI assistant task failed',
    'aiAssistant:settings.title': 'AI Assistant',
    'aiAssistant:settings.description': 'Configure the floating AI chat widget, model, and server-side assistant behavior.',
    'aiAssistant:settings.enabled': 'Enabled',
    'aiAssistant:settings.disabled': 'Disabled',
    'aiAssistant:settings.configured': 'Configured',
    'aiAssistant:settings.apiKeyRequired': 'API key required',
    'aiAssistant:settings.readOnly': 'Read only',
    'aiAssistant:settings.provider': 'Provider: {provider}',
    'aiAssistant:settings.enableLabel': 'Enable assistant',
    'aiAssistant:settings.enableHint': 'Controls the visibility of the floating widget and chat endpoint.',
    'aiAssistant:settings.modelLabel': 'OpenRouter model',
    'aiAssistant:settings.apiKeyLabel': 'OpenRouter API key',
    'aiAssistant:settings.apiKeyHint': 'Leave empty to keep the existing saved key on the server.',
    'aiAssistant:settings.systemPromptLabel': 'System prompt override',
    'aiAssistant:settings.systemPromptPlaceholder': 'Optional project-specific instructions for the assistant',
    'aiAssistant:settings.accessTitle': 'Access model',
    'aiAssistant:settings.accessDescription': 'The current assistant is limited to read-only record inspection through admin resource actions.',
    'aiAssistant:settings.futureToolsReady': 'Future tools ready',
    'aiAssistant:settings.save': 'Save settings',
    'aiAssistant:settings.saving': 'Saving…',
    'aiAssistant:settings.saved': 'AI assistant settings saved',
    'aiAssistant:settings.empty': 'No settings available.',
    'filter:op.co': 'Contains',
    'filter:op.nco': 'Does not contain',
    'filter:op.sw': 'Starts with',
    'filter:op.ew': 'Ends with',
    'filter:op.eq': 'Equals',
    'filter:op.neq': 'Does not equal',
    'filter:op.empty': 'Is empty',
    'filter:op.nempty': 'Is not empty',
    'filter:op.in': 'Is one of',
    'filter:op.gt': 'Greater than',
    'filter:op.lt': 'Less than',
    'filter:op.between': 'Between',
    'filter:searchValues': 'Search in list…',
    'filter:selectAll': 'Select all',
    'filter:noValues': 'No values found',
    'common:newRecord': 'New {name}',
    'common:editRecord': 'Edit {name} #{id}',
    'common:themeLight': 'Light',
    'common:themeDark': 'Dark',
    'common:themeSystem': 'System',
    'common:ok': 'OK',
    'common:select': 'Select…',
    'common:removeItem': 'Remove {title}',
    'common:nSelectedAddMore': '{count} selected — add more',
    'common:pickRecords': 'Pick records',
    'common:managePickRecords': 'Manage selection ({count})',
    'common:pickRecordsFrom': 'Pick from {name}',
    'common:draftRestored': 'Restored unsaved draft',
    'common:undoDraftRestore': 'Undo',
    'aiFill:button': 'AI Fill',
    'aiFill:dialogTitle': 'Fill form from photo',
    'aiFill:dialogDescription': 'Upload an image — AI will extract values and fill the form.',
    'aiFill:fillButton': 'Recognize',
    'aiFill:processing': 'Recognizing image…',
    'aiFill:success': 'Filled {count} field(s)',
    'aiFill:errorNoFields': 'This resource has no AI-fillable fields.',
    'aiFill:errorGeneric': 'AI fill failed: {message}',
    'aiFill:noValues': 'AI could not extract any values from this image.',
    'aiFill:applied': 'AI filled {count} field(s). You can undo below.',
    'wizard:stepOf': 'Step {current} of {total}',
    'wizard:products.step1': 'Basic info',
    'wizard:products.step2': 'Pricing',
    'wizard:products.step3': 'Media & tags',
    'common:invalidValue': 'Invalid value',
    'common:collapseSidebar': 'Collapse sidebar',
    'common:expandSidebar': 'Expand sidebar',
    'common:toggleSidebar': 'Toggle sidebar',
    'common:sidebarTitle': 'Sidebar',
    'common:sidebarNavigation': 'Navigation menu',
    'cache:title': 'Cache',
    'cache:description': 'Runtime cache health and per-resource performance for this replica.',
    'cache:load.error': 'Failed to load cache metrics',
    'cache:load.retry': 'Try again',
    'cache:reset.action': 'Reset metrics',
    'cache:reset.loading': 'Resetting cache metrics…',
    'cache:reset.success': 'Cache metrics reset',
    'cache:reset.error': 'Failed to reset cache metrics',
    'cache:metric.hitRate': 'Hit rate',
    'cache:metric.requests': 'Cache reads',
    'cache:metric.avgCompute': 'Average compute',
    'cache:metric.errors': 'Errors',
    'cache:unit.ms': '{value} ms',
    'cache:quarantine.title': 'Quarantined tags',
    'cache:quarantine.description': 'These tags bypass cache reads until invalidation succeeds.',
    'cache:invalidate.title': 'Invalidate resource cache',
    'cache:invalidate.description': 'Drop list and record entries for a resource across configured replicas.',
    'cache:invalidate.resource': 'Resource',
    'cache:invalidate.action': 'Invalidate cache',
    'cache:invalidate.loading': 'Invalidating cache…',
    'cache:invalidate.success': 'Resource cache invalidated',
    'cache:invalidate.error': 'Failed to invalidate resource cache',
    'cache:table.title': 'Metrics by namespace',
    'cache:table.empty': 'No cache activity recorded yet.',
    'cache:instance': 'Replica {id}',
    'cache:table.namespace': 'Namespace',
    'cache:table.resource': 'Resource',
    'cache:table.hits': 'Hits',
    'cache:table.misses': 'Misses',
    'cache:table.computes': 'Computes',
    'cache:table.coalesced': 'Coalesced',
    'cache:table.errors': 'Errors',
  },
}
